import { ScrollColumnView } from '@ipfabric/ui-components/ScrollView/ScrollColumnView';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { useState } from 'react';
import type { FC, ReactNode, RefObject } from 'react';
import type Scrollbars from 'react-custom-scrollbars-2';
import type { positionValues } from 'react-custom-scrollbars-2';

const SPINNER_HEIGHT = 84;

export interface ScrollPaginatorProps {
  children?: ReactNode;
  loaded: number;
  onFetch: () => Awaitable<void>;
  /**
   * null = nothing loaded yet; show loader but don't fire onFetch in that case
   * (we assume parent component handles initial load differently)
   * undefined = fetch failed; show error, no loader
   */
  total: number | null | undefined;
  scrollViewRef?: RefObject<Scrollbars>;
}

export const ScrollPaginator: FC<ScrollPaginatorProps> = (props) => {
  const {
    generic: {
      components: { scrollPaginator: scrollPaginatorMsg },
    },
  } = useLocale();
  const [fetching, setFetching] = useState<boolean>(false);

  const handleFetch = async () => {
    if (
      !fetching &&
      props.total !== undefined &&
      props.total !== null &&
      props.total > props.loaded
    ) {
      setFetching(true);
      try {
        await props.onFetch();
      } finally {
        setFetching(false);
      }
    }
  };

  return (
    <ScrollColumnView
      className='scroll-paginator'
      onUpdate={({ scrollTop, scrollHeight, clientHeight }: positionValues) => {
        if (scrollTop + SPINNER_HEIGHT > scrollHeight - clientHeight) {
          handleFetch();
        }
      }}
      ref={props.scrollViewRef}
    >
      {props.children}
      {props.total !== undefined &&
        (props.total === null || props.total > props.loaded) && (
          <LoaderAndText noVerticalMargin />
        )}
      {props.total === undefined && (
        <div className='w-full text-center text-red-600'>
          {scrollPaginatorMsg.error}
        </div>
      )}
    </ScrollColumnView>
  );
};
